import { motion, useAnimation } from 'framer-motion';
import { MutableRefObject, ReactElement, useEffect, useRef, useState } from 'react';

function useOnScreen(
    ref: MutableRefObject<HTMLDivElement | null>,
    rootMargin: string = '0px'
): boolean {
    const [isIntersecting, setIntersecting] = useState<boolean>(false);

    useEffect(() => {
        let currentRef: Element | null = null;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) {
                    setIntersecting(entry?.isIntersecting);
                }
            },
            { rootMargin }
        );

        if (ref && ref?.current) {
            currentRef = ref.current;
            observer.observe(currentRef);
        }

        return () => {
            currentRef && observer.unobserve(currentRef);
        };
    }, [ref, rootMargin]); // Empty array ensures that effect is only run on mount and unmount

    return isIntersecting;
}

interface ILazyShow {
    children: ReactElement;
}

export const LazyShow = ({ children }: ILazyShow): ReactElement => {
    const controls = useAnimation();
    const rootRef = useRef<HTMLDivElement | null>(null);
    const onScreen = useOnScreen(rootRef);

    useEffect(() => {
        if (onScreen) {
            controls.start({
                x: 0,
                opacity: 1,
                transition: {
                    duration: 0.5,
                    ease: 'easeOut',
                },
            });
        }
    }, [onScreen, controls]);

    return (
        <motion.div
            className='lazy-div'
            ref={rootRef}
            initial={{ opacity: 0, x: -50 }}
            animate={controls}
        >
            {children}
        </motion.div>
    );
};
